import type {
  GuidedAnswers,
  GuidedFollowUpAnswers,
  GuidedFollowUpId,
  GuidedQuestionId,
  GuidedUnsureFlags,
} from "@/features/blueprints/blueprintTypes";
import { getFollowUpById } from "./guidedFollowUps";
import type { GuidedSaveState, GuidedStep } from "@/features/blueprints/guided/guidedTypes";

type GuidedReviewProps = {
  answers: GuidedAnswers;
  smartFollowupAnswers: GuidedFollowUpAnswers;
  unsureFlags: GuidedUnsureFlags;
  saveState: GuidedSaveState;
  isGenerating: boolean;
  generationMessage?: string;
  error: string | null;
  onEdit: (step: GuidedStep) => void;
  onGenerate: () => void;
  onSaveAndFinishLater: () => void;
};

function joinItems(items: string[] | undefined): string {
  return items && items.length > 0 ? items.join(", ") : "Not specified";
}

function answerText(value: string | undefined): string {
  return value?.trim() || "Not specified";
}

function ReviewCard({
  title,
  children,
  editStep,
  isUnsure,
  onEdit,
}: {
  title: string;
  children: string;
  editStep: GuidedStep;
  isUnsure?: boolean;
  onEdit: (step: GuidedStep) => void;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-950">{title}</h3>
          {isUnsure ? (
            <p className="mt-1 text-xs font-semibold text-amber-700">
              Unsure - we'll suggest sensible defaults.
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => onEdit(editStep)}
          className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
        >
          Edit
        </button>
      </div>
      <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700">
        {children}
      </p>
    </div>
  );
}

function unsureAreas(flags: GuidedUnsureFlags): string {
  const labels: Record<GuidedQuestionId, string> = {
    q1: "App idea",
    q2: "User and problem",
    q3: "Main actions",
    q4: "Screens and pages",
    q5: "Scope limits",
  };

  const areas = (Object.keys(labels) as GuidedQuestionId[]).filter(
    (key) => flags[key],
  );

  return areas.length > 0
    ? areas.map((key) => labels[key]).join(", ")
    : "No unsure areas selected.";
}

function formatFollowUpAnswer(
  id: GuidedFollowUpId,
  answers: GuidedFollowUpAnswers,
): string {
  const followUp = getFollowUpById(id);
  const answer = answers[id];

  if (!followUp || !answer) {
    return "";
  }

  if (answer.skipped) {
    return `${followUp.title}\nSkipped`;
  }

  if (answer.unsure) {
    return `${followUp.title}\nI don't know - use sensible defaults.`;
  }

  return `${followUp.title}\n${answer.answer?.trim() || "Not specified"}`;
}

export function GuidedReview({
  answers,
  smartFollowupAnswers,
  unsureFlags,
  saveState,
  isGenerating,
  generationMessage,
  error,
  onEdit,
  onGenerate,
  onSaveAndFinishLater,
}: GuidedReviewProps) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-indigo-700">Guided Mode</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
            Review your app brief
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
            Check the details below before generation starts.
          </p>
        </div>
        <span className="rounded-md bg-slate-100 px-3 py-1 text-sm font-semibold capitalize text-slate-700">
          {saveState === "idle" ? "Loaded" : saveState}
        </span>
      </div>

      <p className="mt-5 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-900">
        Do not include passwords, private customer data, API keys, or
        confidential business information.
      </p>

      <div className="mt-5 grid gap-4">
        <ReviewCard title="Project name" editStep="q1" onEdit={onEdit}>
          {answerText(answers.q1?.projectName)}
        </ReviewCard>
        <ReviewCard title="App idea" editStep="q1" onEdit={onEdit}>
          {answerText(answers.q1?.appIdea)}
        </ReviewCard>
        <ReviewCard
          title="User and problem"
          editStep="q2"
          isUnsure={unsureFlags.q2}
          onEdit={onEdit}
        >
          {[answerText(answers.q2?.text), joinItems(answers.q2?.chips)].join(
            "\n",
          )}
        </ReviewCard>
        <ReviewCard
          title="Main actions"
          editStep="q3"
          isUnsure={unsureFlags.q3}
          onEdit={onEdit}
        >
          {[joinItems(answers.q3?.chips), answerText(answers.q3?.extraText)].join(
            "\n",
          )}
        </ReviewCard>
        <ReviewCard
          title="Screens and pages"
          editStep="q4"
          isUnsure={unsureFlags.q4}
          onEdit={onEdit}
        >
          {joinItems(answers.q4?.chips)}
        </ReviewCard>
        <ReviewCard
          title="Scope limits"
          editStep="q5"
          isUnsure={unsureFlags.q5}
          onEdit={onEdit}
        >
          {[joinItems(answers.q5?.chips), answerText(answers.q5?.extraText)].join(
            "\n",
          )}
        </ReviewCard>
        <ReviewCard title="Additional notes" editStep="extra" onEdit={onEdit}>
          {answerText(answers.extraDetail)}
        </ReviewCard>
        {Object.keys(smartFollowupAnswers).length > 0 ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-950">
              Smart follow-ups
            </h3>
            <div className="mt-3 grid gap-3">
              {(Object.keys(smartFollowupAnswers) as GuidedFollowUpId[]).map(
                (id) => (
                  <div
                    key={id}
                    className="whitespace-pre-wrap rounded-md bg-white p-3 text-sm leading-6 text-slate-700"
                  >
                    {formatFollowUpAnswer(id, smartFollowupAnswers)}
                  </div>
                ),
              )}
            </div>
          </div>
        ) : null}
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h3 className="text-sm font-semibold text-amber-950">Unsure areas</h3>
          <p className="mt-2 text-sm leading-6 text-amber-900">
            {unsureAreas(unsureFlags)}
          </p>
        </div>
      </div>

      {error ? (
        <div className="mt-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {isGenerating ? (
        <div className="mt-5 rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900">
          <p className="font-semibold">{generationMessage}</p>
          <p className="mt-1">This usually takes about 20-30 seconds.</p>
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap justify-between gap-2">
        <button
          type="button"
          onClick={onSaveAndFinishLater}
          disabled={isGenerating}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Save and Finish Later
        </button>
        <button
          type="button"
          onClick={onGenerate}
          disabled={isGenerating}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
        >
          {isGenerating ? "Generating..." : "Looks Good - Generate My Blueprint"}
        </button>
      </div>
    </section>
  );
}
